import isEqual from 'lodash/isEqual';
import React, { FC } from 'react';

import { ProblemCanvas, ProblemCanvasOwner, ProblemData } from '@gmm/problem';

interface Props {
  problemData: ProblemData;
  problemCanvasOwner: ProblemCanvasOwner;
  userId: number;
  globalFocus: boolean;
}

export const MemoizedProblemCanvas: FC<Props> = React.memo(
  ({ problemData, problemCanvasOwner, userId, globalFocus }) => {
    return (
      <ProblemCanvas
        problemData={{ r: problemData.r }}
        problemCanvasOwner={problemCanvasOwner}
        userId={userId}
        globalFocus={globalFocus}
      />
    );
  },
  // memoization calls this on re-render to decide whether to rebuild. False means rebuild.
  (prevProps, nextProps) => {
    // Compare userId, globalFocus, and problemCanvasOwner
    const arePropsEqual =
      prevProps.userId === nextProps.userId &&
      prevProps.globalFocus === nextProps.globalFocus &&
      prevProps.problemCanvasOwner === nextProps.problemCanvasOwner;

    // If other props are not equal, no need to check problemData.r
    if (!arePropsEqual) return false;

    // Compare problemData.r with a custom function focusing on 'ag'.
    // We need to detect changes to attempt getters to re-render if a student
    // has changed their exam answer (which includes answering for the first time).
    return compareProblemDataR(
      prevProps.problemData.r,
      nextProps.problemData.r,
    );
  },
);

// Custom comparison function for problemData.r
function compareProblemDataR(prevR: any[][], nextR: any[][]): boolean {
  // If the outer arrays have different lengths, they are different
  if (prevR.length !== nextR.length) return false;

  // Compare each inner array
  for (let i = 0; i < prevR.length; i++) {
    const prevInner = prevR[i];
    const nextInner = nextR[i];

    // If inner arrays have different lengths, they are different
    if (prevInner.length !== nextInner.length) return false;

    // Check if this inner array has any objects with 'ag'
    const hasAg = prevInner.some(obj => 'ag' in obj);

    // If no 'ag' in this inner array, skip detailed comparison
    if (!hasAg) continue;

    // Compare objects in the inner array
    for (let j = 0; j < prevInner.length; j++) {
      const prevObj = prevInner[j];
      const nextObj = nextInner[j];

      // If one object has 'ag' and the other doesn't, they are different
      const prevHasAg = 'ag' in prevObj;
      const nextHasAg = 'ag' in nextObj;

      if (prevHasAg !== nextHasAg) return false;

      // If both objects have 'ag', compare 's' (status)
      if (prevHasAg && nextHasAg) {
        if (!isEqual(prevObj.ag, nextObj.ag)) {
          return false;
        }
      }
    }
  }

  return true;
}

MemoizedProblemCanvas.displayName = 'MemoizedProblemCanvas';
